import isFunc from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import has from 'lodash/has';
import get from 'lodash/get';
import set from 'lodash/set';

/**
 * The relate function is used to merge relationship data into the main data objects.
 * It extracts relationships from the response and maps them to the main data based on field configurations.
 * 
 * @param {Array|Object} fields - Configuration for how to relate data. Can be:
 *   - Object: { sourceField: targetField }
 *   - Array of arrays: [[sourceField, targetField], ...]
 *   - Functions can be used for dynamic field resolution
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.beforeRelate - Function to extract relationships from response data
 * 
 * USAGE EXAMPLES:
 * 
 * 1. BASIC STRING MAPPING:
 * // Maps user_id from main data to user field using relationships
 * relate([['user_id', 'user']])
 * 
 * // Input response:
 * {
 *   data: {
 *     data: [{ id: 1, user_id: 'user_123', title: 'Post 1' }],
 *     relationships: {
 *       'user_123': { id: 'user_123', name: 'John Doe', email: 'john@example.com' }
 *     }
 *   }
 * }
 * // Output: [{ id: 1, user_id: 'user_123', title: 'Post 1', user: { id: 'user_123', name: 'John Doe', email: 'john@example.com' } }]
 * 
 * 2. OBJECT NOTATION:
 * // Same as above but using object syntax
 * relate({ user_id: 'user', category_id: 'category' })
 * 
 * 3. NESTED PATH MAPPING:
 * // Maps nested paths
 * relate([['author.id', 'author_details']])
 * 
 * 4. ARRAY KEY EXTRACTION (@):
 * // When relationships is an array, use @ to specify which field to use as key
 * relate([['user_id@id', 'user']])
 * 
 * // Input with array relationships:
 * {
 *   data: {
 *     data: [{ id: 1, user_id: 'user_123' }],
 *     relationships: [
 *       { id: 'user_123', name: 'John' },
 *       { id: 'user_456', name: 'Jane' }
 *     ]
 *   }
 * }
 * 
 * 5. FUNCTION-BASED MAPPING:
 * // Dynamic source field resolution
 * relate([
 *   [(item) => item.owner_type === 'user' ? item.owner_id : null, 'owner']
 * ])
 * 
 * // Dynamic source field resolution with dynamic field name
 * relate([
 *   [(item) => {
 *     return `context:${item.id}`
 *   }, (r) => {
 *     return 'context'
 *   }]
 * ])
 * 
 * // Dynamic target field resolution
 * relate([
 *   ['user_id', (item) => `${item.type}_user`]
 * ])
 * 
 * 6. MULTIPLE RELATIONSHIPS:
 * relate([
 *   ['user_id', 'user'],
 *   ['category_id', 'category'],
 *   ['tags', 'tag_details']
 * ])
 * 
 * 7. COMPLEX EXAMPLE WITH CUSTOM beforeRelate:
 * relate([['author_id', 'author']], {
 *   beforeRelate: (data) => data.included // For JSON:API format
 * })
 * 
 * 8. CONDITIONAL RELATIONSHIPS:
 * relate([
 *   [(item) => item.assignee_id || item.creator_id, 'responsible_user']
 * ])
 * 
 * COMMON USE CASES:
 * - API responses with separate relationships object (like JSON:API)
 * - Normalizing nested data structures
 * - Hydrating references with full objects
 * - Converting foreign keys to embedded objects
 * - Merging relationships into the main data (using '...' as target field)
 */

const join = (list, separator = '::') =>
    list.filter(v => !!v).join(separator);

const useKey = (path) => {
    if (typeof path !== 'string') return { key: undefined, at: undefined };

    const [key, at] = path.split('@');

    return { key, at }
};

const defaultOptions = {
    beforeRelate: (data) => data.relationships || data.relationship || [],
}

const createDumper = (debug = false) => new Proxy({}, {
    get: (_, prop) => (...args) => {
        if (!debug) return;

        switch (prop) {
            case 'start':
                console.group(args[0]);
                break;
            case 'end':
                args[0] && console.log(args[0]);
                console.groupEnd();
                break;
            default:
                console[prop](...args);
                break;
        }

        return this;
    }
})

const assertFieldsIsArray = (fields) => {
    if (!isArray(fields) && isObject(fields)) {
        fields = Object.entries(fields);
    }

    return fields;
}

export const relate = (fields = [], options = {}) => (response) => {
    const { beforeRelate, debug = false } = Object.assign(defaultOptions, options);
    const { data: base, config } = response;

    //#region Debugging
    const dump = createDumper(debug);
    dump.start(`[dumper] ${config.url || 'Debugging'}`)
    dump.log('Base data:', base)
    //#endregion

    // if the response is not an object, return it
    let relationships = beforeRelate(base);

    //#region Debugging
    dump.log('Extracted relationships (raw):', relationships)
    dump.log('Fields configuration:', fields)
    //#endregion

    if (!fields || isEmpty(relationships)) {
        //#region Debugging
        dump.warn('Early return:', {
            hasFields: !!fields,
            hasRelationships: !isEmpty(relationships),
            reason: !fields ? 'No fields provided' : 'No relationships found'
        })
        dump.end();
        //#endregion
        return response;
    }



    // if the relationships is an array, we need to reduce it to an object
    if (isArray(relationships)) {
        //#region Debugging
        dump.log('Relationships is an array, converting to object...')
        //#endregion
        
        relationships = relationships.reduce((acc, target, index) => {
            //#region Debugging
            dump.log(`  Processing relationship item ${index + 1}:`, target)
            //#endregion

            let normalizedFields = fields;
            if (isFunc(fields)) {
                normalizedFields = fields(target);
                //#region Debugging
                dump.log('Normalized fields:', normalizedFields)
                //#endregion
            }

            const resolvedFields = assertFieldsIsArray(normalizedFields);
            if (!resolvedFields || isEmpty(resolvedFields)) return acc;

            resolvedFields.forEach(([from, to], fieldIndex) => {
                let originalFrom = from;
                
                if (isFunc(from)) {
                    from = from(target);
                    //#region Debugging
                    dump.log(`    Field ${fieldIndex + 1}: Function resolved "${originalFrom.name || 'anonymous'}" → "${from}"`)
                    //#endregion
                }

                // function mappers may return a nullish key (e.g. optional co_author_id)
                if (!from || typeof from !== 'string') return;

                const { at } = useKey(from);

                if (at && has(target, at)) {
                    const key = join([at, get(target, at)]);
                    acc[key] = target;
                    
                    //#region Debugging
                    dump.log(`    [OK] Created key "${key}" from field "${at}" = "${get(target, at)}"`)
                    //#endregion
                }
                
                //#region Debugging
                if (!at || !has(target, at)) {
                    dump.log(`    [SKIP] ${at ? `field "${at}" not found in target` : 'no @ notation in field'}`)
                }
                //#endregion
            })
            
            return acc;
        }, {});
        
        //#region Debugging
        dump.log('Converted relationships object:', relationships)
        dump.log(`   Total keys: ${Object.keys(relationships).length}`)
        //#endregion
    }

    // relate the data from the fields
    const relationship = (target, targetIndex) => {
        //#region Debugging
        dump.info(`Processing target ${targetIndex + 1}:`, target)
        //#endregion
        let normalizedFields = fields;
        
        if (isFunc(fields)) {
            normalizedFields = fields(target);
            //#region Debugging
            dump.log('Normalized fields:', normalizedFields)
            //#endregion
        }

        const resolvedFields = assertFieldsIsArray(normalizedFields);
        if (!resolvedFields || isEmpty(resolvedFields)) return;

        resolvedFields.forEach(([from, to], fieldIndex) => {
            //#region Debugging
            dump.log(`  Field ${fieldIndex + 1}: [${typeof from === 'function' ? 'function' : `"${from}"`}, ${typeof to === 'function' ? 'function' : `"${to}"`}]`)
            //#endregion

            let path = isFunc(from) ? from(target) : from;
            let fromKey = null;
            
            //#region Debugging
            if (isFunc(from)) {
                dump.log(`    Function resolved source path: "${path}"`)
            }
            //#endregion
            
            // If not a direct key, extract from target
            if (path?.includes('@')) {
                const { key, at } = useKey(path);
                
                //#region Debugging
                dump.log(`    @ notation detected: key="${key}", at="${at}"`)
                //#endregion

                if (!has(target, key)) {
                    //#region Debugging
                    dump.log(`    [SKIP] key "${key}" not found in target`)
                    //#endregion
                    return;
                }
                
                const value = get(target, key);

                //#region Debugging
                if (!path?.includes('@')) {
                    dump.log(`    Using path "${path}" to get value: "${value}"`)
                }
                //#endregion
                
                fromKey = join([at, value]);
                
                //#region Debugging
                dump.log(`    [OK] Built path "${path}" from key "${key}" = "${fromKey}"`)
                //#endregion
            } else {
                fromKey = get(target, path, path /* fallback to path if not found */);
                //#region Debugging
                dump.log(`    [OK] Built fromKey: "${fromKey}" from path "${path}"`)
                //#endregion
            }
            
            // Look up and attach relationship
            if (!(fromKey && isObject(relationships) && has(relationships, fromKey))) {
                //#region Debugging
                dump.log(`    [SKIP] Relationship not found for path "${path}"`)
                dump.log(`      Available paths:`, Object.keys(relationships).slice(0, 10))
                //#endregion
                return;
            }
            
            const value = relationships[fromKey];
            const toKey = isFunc(to) ? to(target) : to;
            
            //#region Debugging
            dump.log(`    [OK] Found relationship for path "${path}"`)
            dump.log(`    Relationship value:`, value)
            dump.log(`    Target key: "${toKey}"`)
            //#endregion
            
            if (toKey === '...') {
                merge(target, value);
                //#region Debugging
                dump.log(`    [OK] Merged relationship into target (spread operator)`)
                //#endregion
            }
            
            if (toKey !== '...') {
                set(target, toKey, value);
                //#region Debugging
                dump.log(`    [OK] Set relationship at path "${toKey}"`)
                //#endregion
            }
        })
        
        //#region Debugging
        dump.log(`  Final target after relationships:`, target)
        //#endregion
    }

    // iterate over each one to relate objects
    const dataArray = isArray(base.data) ? base.data : [base.data];
    const filteredData = dataArray.filter(v => !!v);
    
    //#region Debugging
    dump.log(`Processing ${filteredData.length} target(s) from ${dataArray.length} item(s)`)
    //#endregion

    filteredData.forEach(relationship);

    //#region Debugging
    dump.end('Final response:', response)
    //#endregion

    return response;
}
